/**
 * The issues a GitHub Actions drift workflow reports — not the backend.
 *
 * Each code here is reported by exactly one workflow, through the OIDC-authenticated ingest route
 * (`apps/backend/src/issue-ingest/`), and only that workflow may report it:
 * `WORKFLOW_ISSUE_GRANTS` binds the two. Each is PLATFORM-scoped — a database, an environment or
 * an AWS account belongs to no tenant — and each resolves when the workflow's next run is green,
 * so the row's own lifecycle is the alarm's: one Slack line when it opens or reopens, silence
 * while it repeats, and silence when it clears.
 *
 * ## Why all three PAGE
 *
 * Each runs on a schedule nobody watches, against infrastructure no request touches, and each has
 * already been red for days with nobody reading it — the run lands in the Actions tab and nowhere
 * else. The admin Issues page is the record, but it is a page an operator opens when they already
 * suspect something; these three find the thing nobody suspects. A page fires once per OPENED or
 * REOPENED (never on a repeat), and the schedules are nightly, hourly and daily, so the most any
 * of them can say is one line per incident.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

export const WORKFLOW_DRIFT_ISSUES = {
  /**
   * A live database no longer equals the migrations its environment deploys
   * (`.github/workflows/schema-drift.yml`, nightly).
   *
   * Fingerprint: `entityType: database`, `entityId` the workflow's matrix row — `prod`,
   * `echo-prod`, `staging`, `echo-staging`, `dev`. The two Prisma packages of one environment are
   * two rows because they drift and are repaired independently, and production's prisma-db being
   * behind says nothing about its echo database.
   *
   * page: a production database diverging from `main` is either a deploy that never happened or
   * an object somebody created by hand, and the NEXT deploy is where it bites — a migration that
   * collides with the hand-made object fails the predeploy and the whole release with it. Someone
   * must act before that deploy, not when they next open the Issues page.
   */
  SCHEMA_DRIFT_DETECTED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A live database no longer equals the migrations its environment deploys. The nightly schema-drift check compared it against a reference built from that environment's branch and found unfiled differences — a migration that never applied, an object created by hand, an object enforced twice, or an exclusion that stopped matching. The issue payload carries the run URL and the finding rows.",
    severity: "high",
    page: true,
    remedy:
      "Open the run linked in the issue payload (`run.url`) and read its summary for this environment. " +
      "Check A rows (PENDING / FAILED / CHECKSUM) are a deploy that has not happened — look at the Porter predeploy for that environment first, then `pnpm db:migrate` there through the normal release, never by hand. " +
      "Check B rows with Check A clean are an object somebody created by hand: either write the migration that owns it or drop it, through `whiterose`'s migration review. " +
      "Check C rows are an object enforced twice. Check D rows are an entry in `scripts/db/schema-drift/exclusions.tsv` that stopped matching — usually its issue was fixed and the line should be deleted. " +
      "The row clears itself the next time the nightly run for this database is green; ignore it on the Issues page only for a database that is known-broken and has its own ticket."
  },
  /**
   * The secret store is not reaching an environment's backend pods
   * (`.github/workflows/secret-delivery-drift.yml`, hourly).
   *
   * Fingerprint: `entityType: deployment_environment`, `entityId` the environment slug — `prod`,
   * `staging`, `dev`. The check walks one environment's whole delivery chain (store → operator →
   * Kubernetes secret → pod), and the chain has no owner finer than the environment.
   *
   * page: a production freeze — a secret rotated in the store that never reaches the pods — was
   * re-derived by hand over eight hours on 2026-08-30 while nine alerts about it went unread. A
   * pod running on a stale or missing secret fails at the moment that secret is next used, which
   * is a customer request.
   */
  SECRET_DELIVERY_DRIFT_DETECTED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The secret store is not what this environment's backend is being given. The hourly secret-delivery check found the Infisical store, the operator, the Kubernetes secret or the running pods out of step — a sync that stopped, a name the operator cannot reach, or a mounted secret older than the store. The issue payload carries the run URL and the checker's verdict lines.",
    severity: "high",
    page: true,
    remedy:
      "Open the run linked in the issue payload (`run.url`) and read the `delivery: <env>` job's summary: it names the hop that disagrees. " +
      "A SYNC-DEAD or stale hop-3 verdict means the Infisical operator stopped reconciling — read its log on the cluster the summary names before restarting anything. " +
      "A name present in the store and absent from the pod means the env group the app mounts does not carry it — fix the env group, then redeploy so the pods pick it up. " +
      "Do not rotate a secret to 'fix' this: a rotation that cannot be delivered is the failure, repeated. The row clears itself on the next green hourly run for this environment."
  },
  /**
   * The live Vibe AWS account no longer equals `deployment/vibe-iam/`, or a check guarding that
   * comparison went red (`.github/workflows/vibe-iam-drift.yml`, daily).
   *
   * Fingerprint: `entityType: aws_account`, `entityId: vibe-<stage>` — the account's logical name
   * in `deployment/aws-accounts.json` and the stage of the leg that judged it. The workflow runs
   * one leg per stage, each against the tree that stage is published from, and each leg reports
   * and resolves its own row only — so a green staging leg can never clear a red prod one.
   *
   * page: this account's IAM is what bounds every tenant's control-plane identity. A role or
   * policy that changed out of band is a privilege change nobody reviewed, and the daily run is
   * the only thing that looks.
   */
  VIBE_IAM_DRIFT_DETECTED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The live Vibe AWS account's IAM no longer equals deployment/vibe-iam/, or one of the checks guarding that comparison failed. The daily vibe-iam drift run went red; the issue payload names the jobs that failed and links the run.",
    severity: "high",
    page: true,
    remedy:
      "Open the run linked in the issue payload (`run.url`). If `live account == deployment/vibe-iam` failed, its summary lists every FAIL row: a row that is new since the last scheduled run (that leg's durability verdict, `NEW`) is an out-of-band change — find who made it in CloudTrail before reverting it, then republish through `vibe-iam-publish.yml`. " +
      "If only a `the check can still fail` job is red, nothing drifted: the comparator itself stopped being falsifiable, and the fix is in `deployment/vibe-iam/`, not in AWS. " +
      "The row clears itself on the next green daily run of its stage's leg."
  }
} as const satisfies Record<string, IssueDefinition>;
